/**
 * Lightweight localStorage quota helper.
 *
 * Browsers cap localStorage at ~5–10MB and throw QuotaExceededError when
 * setItem() exceeds the cap. We wrap writes so quota errors surface as a
 * named signal callers can react to, and provide a coarse "how full are we"
 * estimate to warn users before they hit the wall.
 */

import { logger } from "./logger"

const WARNING_THRESHOLD = 0.85 // Warn at 85% utilization
const ASSUMED_QUOTA_BYTES = 5 * 1024 * 1024

export interface StorageQuotaStatus {
  used: number
  estimatedQuota: number
  ratio: number
  approachingLimit: boolean
}

export class StorageQuotaError extends Error {
  constructor(public readonly key: string) {
    super(`Storage quota exceeded while writing "${key}"`)
    this.name = "StorageQuotaError"
  }
}

/** Approximate bytes used by localStorage (sum of key + value lengths × 2 for UTF-16). */
export function getStorageUsage(): number {
  if (typeof localStorage === "undefined") return 0
  let bytes = 0
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key === null) continue
    const value = localStorage.getItem(key)
    bytes += (key.length + (value?.length ?? 0)) * 2
  }
  return bytes
}

export function getStorageStatus(): StorageQuotaStatus {
  const used = getStorageUsage()
  const ratio = used / ASSUMED_QUOTA_BYTES
  return {
    used,
    estimatedQuota: ASSUMED_QUOTA_BYTES,
    ratio,
    approachingLimit: ratio >= WARNING_THRESHOLD,
  }
}

function isQuotaError(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  return (
    err.name === "QuotaExceededError" ||
    err.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    err.message.toLowerCase().includes("quota")
  )
}

/** Wrap a localStorage write so callers can distinguish quota failures from other I/O errors. */
export function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch (err) {
    if (isQuotaError(err)) {
      logger.warn("localStorage quota exceeded", { key, bytes: value.length })
      throw new StorageQuotaError(key)
    }
    throw err
  }
}

// Outstanding deferred writes, keyed by storage key. A rapid burst of mutations
// (e.g. flipping notification-preference toggles) collapses into a single
// localStorage write per idle tick instead of one per change. The value
// factory is kept alongside the timer so a flush can execute the write early.
const pendingWrites = new Map<string, { timer: ReturnType<typeof setTimeout>; getValue: () => string }>()

function runWrite(key: string, getValue: () => string): void {
  try {
    safeSetItem(key, getValue())
  } catch (err) {
    if (err instanceof StorageQuotaError) return
    logger.error("Deferred localStorage write failed", err)
  }
}

/**
 * Coalesce a localStorage write: the value-factory is called once when the
 * timer fires, so callers always persist the latest state without thrashing
 * JSON.stringify on every keystroke or toggle. Quota errors are swallowed and
 * logged — the in-memory state is already updated, and re-saving on the next
 * change is enough.
 */
export function scheduleSave(key: string, getValue: () => string, delayMs = 250): void {
  const existing = pendingWrites.get(key)
  if (existing) clearTimeout(existing.timer)
  const timer = setTimeout(() => {
    pendingWrites.delete(key)
    runWrite(key, getValue)
  }, delayMs)
  pendingWrites.set(key, { timer, getValue })
}

/** Force any pending deferred write to run now (e.g. on tab close). */
export function flushScheduledSaves(): void {
  for (const [key, pending] of pendingWrites) {
    clearTimeout(pending.timer)
    runWrite(key, pending.getValue)
  }
  pendingWrites.clear()
}
